if (!customElements.get('paint-project-carousel')) {
  customElements.define('paint-project-carousel', class PaintProjectCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.scroller = this.querySelector('[data-carousel-scroller]');
      this.previousButton = this.querySelector('[data-carousel-previous]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.toggleButton = this.querySelector('[data-carousel-toggle]');
      this.toggleIcon = this.querySelector('[data-carousel-toggle-icon]');
      this.toggleLabel = this.querySelector('[data-carousel-toggle-label]');
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.interval = Math.max(Number(this.dataset.interval) || 6000, 4000);
      this.manualPaused = false;
      this.interacting = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!this.scroller) return;
      this.bindEvents();
      this.updateControls();
      this.updateAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.resizeObserver?.disconnect();
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', () => this.move(-1));
      this.nextButton?.addEventListener('click', () => this.move(1));
      this.toggleButton?.addEventListener('click', () => {
        this.manualPaused = !this.manualPaused;
        this.updateToggle();
        this.updateAutoplay();
      });
      this.scroller.addEventListener('scroll', () => this.updateControls(), { passive: true });
      this.scroller.addEventListener('pointerdown', () => this.pauseAfterManualNavigation(), { passive: true });
      this.scroller.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      });
      this.addEventListener('pointerenter', () => { this.interacting = true; this.updateAutoplay(); });
      this.addEventListener('pointerleave', () => { this.interacting = false; this.updateAutoplay(); });
      this.addEventListener('focusin', () => { this.interacting = true; this.updateAutoplay(); });
      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.interacting = false;
        this.updateAutoplay();
      });
      document.addEventListener('visibilitychange', () => this.updateAutoplay());
      this.resizeObserver = new ResizeObserver(() => this.updateControls());
      this.resizeObserver.observe(this.scroller);
    }

    cardStep() {
      const firstCard = this.scroller.querySelector('.paint-project-gallery__item');
      if (!firstCard) return this.scroller.clientWidth;
      const list = firstCard.parentElement;
      const gap = Number.parseFloat(getComputedStyle(list).columnGap) || 0;
      return firstCard.getBoundingClientRect().width + gap;
    }

    pauseAfterManualNavigation() {
      if (!this.autoplayEnabled) return;
      this.manualPaused = true;
      this.updateToggle();
      this.updateAutoplay();
    }

    move(direction, manual = true) {
      if (manual) this.pauseAfterManualNavigation();
      this.scroller.scrollBy({
        left: this.cardStep() * direction,
        behavior: this.reducedMotion ? 'auto' : 'smooth'
      });
    }

    updateControls() {
      const maxScroll = Math.max(this.scroller.scrollWidth - this.scroller.clientWidth, 0);
      if (this.previousButton) this.previousButton.disabled = this.scroller.scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = this.scroller.scrollLeft >= maxScroll - 2;
    }

    updateToggle() {
      if (!this.toggleButton) return;
      const paused = this.manualPaused;
      this.toggleButton.setAttribute('aria-pressed', String(paused));
      this.toggleIcon.textContent = paused ? '▶' : 'Ⅱ';
      this.toggleLabel.textContent = paused ? 'Resume automatic rotation' : 'Pause automatic rotation';
    }

    updateAutoplay() {
      const shouldRun = this.autoplayEnabled && !this.reducedMotion && !this.manualPaused && !this.interacting && !document.hidden;
      this.toggleButton?.toggleAttribute('hidden', !this.autoplayEnabled || this.reducedMotion);
      if (shouldRun) this.startAutoplay();
      else this.stopAutoplay();
    }

    startAutoplay() {
      if (this.timer) return;
      this.timer = window.setInterval(() => {
        const maxScroll = Math.max(this.scroller.scrollWidth - this.scroller.clientWidth, 0);
        if (this.scroller.scrollLeft >= maxScroll - 2) {
          this.scroller.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.move(1, false);
        }
      }, this.interval);
    }

    stopAutoplay() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }
  });
}
